'use client';

/**
 * QuickCommandsMenu Component
 * 
 * REQUIREMENTS SUMMARY:
 * 1. Displays built-in and custom quick commands for the chatbot
 * 2. Allows users to create, edit, and delete their own quick commands
 * 3. Tracks usage when commands are clicked
 * 4. Popover-style menu anchored to the quick commands button
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { cn } from '@/utils/common/helpers';
import { useAuth } from '@/context/AuthContext';
import { useOrganization } from '@/context/OrganizationContext';
import { createLogger } from '@/utils/logger/devLogger';
import { useDemoGuard } from '@/hooks/ui/useDemoGuard';

const logger = createLogger('QuickCommandsMenu');

interface CustomQuickCommand {
  id: string;
  label: string;
  command_text: string;
  icon: string;
  color: string;
  usage_count: number;
  display_order: number;
}

interface QuickCommandsMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onUseCommand: (commandText: string, commandLabel: string) => void;
  disabled?: boolean;
}

export function QuickCommandsMenu({
  isOpen,
  onClose,
  onUseCommand,
  disabled = false,
}: QuickCommandsMenuProps) {
  const { user, session } = useAuth();
  const { currentOrganization } = useOrganization();
  const { blockIfDemo } = useDemoGuard();
  
  const [customCommands, setCustomCommands] = useState<CustomQuickCommand[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNewCommandForm, setShowNewCommandForm] = useState(false);
  const [newCommandLabel, setNewCommandLabel] = useState('');
  const [newCommandText, setNewCommandText] = useState('');
  const [savingCommand, setSavingCommand] = useState(false);

  // Built-in quick commands (always available)
  const builtInCommands = [
    { label: 'Report a Bug', icon: 'bug_report', color: 'error' },
    { label: 'Suggest a Feature', icon: 'lightbulb', color: 'brand' },
    { label: 'Share Feedback', icon: 'rate_review', color: 'gray' },
  ];

  // Fetch custom quick commands
  const fetchCustomCommands = useCallback(async () => {
    if (!user?.id || !currentOrganization?.id) return;
    
    setLoading(true);
    try {
      const response = await fetch('/api/chat/quick-commands', {
        headers: {
          'Content-Type': 'application/json',
          'x-organization-id': currentOrganization.id,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCustomCommands(data.commands || []);
        }
      } else {
        logger.error('Failed to fetch quick commands:', {
          status: response.status,
          statusText: response.statusText,
        });
      }
    } catch (error: any) {
      logger.error('Failed to fetch quick commands:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, currentOrganization?.id]);

  // Load commands when menu opens
  useEffect(() => {
    if (isOpen) {
      fetchCustomCommands();
    }
  }, [isOpen, fetchCustomCommands]);

  // Handle using a command
  const handleUseCommand = async (commandText: string, commandLabel: string, commandId?: string) => {
    // If it's a custom command, increment usage count
    if (commandId) {
      try {
        await fetch(`/api/chat/quick-commands/${commandId}/use`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-organization-id': currentOrganization?.id || '',
          },
        });
      } catch (error) {
        logger.warn('Failed to track command usage:', error);
      }
    }

    onUseCommand(commandText, commandLabel);
    onClose();
  };

  // Handle creating new command
  const handleCreateCommand = async () => {

    if (blockIfDemo()) return;
    if (!newCommandLabel.trim() || !newCommandText.trim() || !currentOrganization?.id) {
      logger.warn('Cannot create command - missing required fields', {
        hasLabel: !!newCommandLabel.trim(),
        hasText: !!newCommandText.trim(),
        hasOrg: !!currentOrganization?.id,
      });
      return;
    }
    
    setSavingCommand(true);
    try {
      logger.info('Creating quick command:', {
        label: newCommandLabel.trim(),
        textLength: newCommandText.trim().length,
      });

      const response = await fetch('/api/chat/quick-commands', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-organization-id': currentOrganization.id,
        },
        body: JSON.stringify({
          label: newCommandLabel.trim(),
          command_text: newCommandText.trim(),
          icon: 'chat',
          color: 'brand',
        }),
      });

      logger.info('Create command response:', {
        status: response.status,
        ok: response.ok,
      });

      if (response.ok) {
        const data = await response.json();
        logger.info('Create command data:', data);
        
        if (data.success) {
          setNewCommandLabel('');
          setNewCommandText('');
          setShowNewCommandForm(false);
          await fetchCustomCommands();
        } else {
          logger.error('Create command failed:', data.error);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        logger.error('Create command HTTP error:', {
          status: response.status,
          error: errorData.error,
        });
      }
    } catch (error: any) {
      logger.error('Failed to create command:', error);
    } finally {
      setSavingCommand(false);
    }
  };

  // Handle deleting a command
  const handleDeleteCommand = async (commandId: string) => {

    if (blockIfDemo()) return;
    if (!currentOrganization?.id) return;
    
    try {
      const response = await fetch(`/api/chat/quick-commands/${commandId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-organization-id': currentOrganization.id,
        },
      });

      if (response.ok) {
        setCustomCommands(prev => prev.filter(cmd => cmd.id !== commandId));
      }
    } catch (error: any) {
      logger.error('Failed to delete command:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[999998]"
      />

      {/* Menu */}
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="absolute bottom-16 left-3 right-3 sm:left-14 sm:right-auto sm:w-80 z-[999999] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-96 overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GoogleMaterialIcon name="bolt" size="sm" className="text-brand-600 dark:text-brand-400" />
            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Quick Commands</h4>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
          >
            <GoogleMaterialIcon name="close" size="xs" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Built-in Commands */}
          <div className="p-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2 mb-2">
              Built-in
            </p>
            <div className="space-y-1">
              {builtInCommands.map((cmd) => (
                <button
                  key={cmd.label}
                  onClick={() => handleUseCommand(cmd.label, cmd.label)}
                  disabled={disabled}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg',
                    'hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
                    'text-left group',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  <div className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center',
                    cmd.color === 'brand' ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400' :
                    cmd.color === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                    'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  )}>
                    <GoogleMaterialIcon name={cmd.icon} size="xs" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                    {cmd.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Commands */}
          {customCommands.length > 0 && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2 mb-2">
                My Commands
              </p>
              <div className="space-y-1">
                {customCommands.map((cmd) => (
                  <div
                    key={cmd.id}
                    className="group flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <button
                      onClick={() => handleUseCommand(cmd.command_text, cmd.label, cmd.id)}
                      disabled={disabled}
                      className="flex-1 flex items-center gap-3 text-left disabled:opacity-50"
                    >
                      <div className="w-8 h-8 rounded-lg bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 flex items-center justify-center">
                        <GoogleMaterialIcon name={cmd.icon} size="xs" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white truncate">
                          {cmd.label}
                        </p>
                        {cmd.usage_count > 0 && (
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            Used {cmd.usage_count} {cmd.usage_count === 1 ? 'time' : 'times'}
                          </p>
                        )}
                      </div>
                    </button>
                    <button
                      onClick={() => handleDeleteCommand(cmd.id)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-all"
                      title="Delete command"
                    >
                      <GoogleMaterialIcon name="delete" size="xs" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Command Form */}
          {showNewCommandForm && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2 mb-3">
                Create New Command
              </p>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Button label (e.g., Check Schedule)"
                  value={newCommandLabel}
                  onChange={(e) => setNewCommandLabel(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-brand-400"
                  maxLength={50}
                />
                <textarea
                  placeholder="Message to send (e.g., What's on my schedule this week?)"
                  value={newCommandText}
                  onChange={(e) => setNewCommandText(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-brand-400 resize-none"
                  maxLength={200}
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCreateCommand}
                    disabled={!newCommandLabel.trim() || !newCommandText.trim() || savingCommand}
                    className="flex-1 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingCommand ? 'Saving...' : 'Create'}
                  </button>
                  <button
                    onClick={() => {
                      setShowNewCommandForm(false);
                      setNewCommandLabel('');
                      setNewCommandText('');
                    }}
                    className="px-3 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Add New Command Button */}
        {!showNewCommandForm && (
          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowNewCommandForm(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors"
            >
              <GoogleMaterialIcon name="add" size="xs" />
              <span>Create Custom Command</span>
            </button>
          </div>
        )}
      </motion.div>
    </>
  );
}

export default QuickCommandsMenu;
